import { collection, addDoc, getDocs, getDoc, doc, query, where, updateDoc, orderBy, limit } from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';
import { useCart } from '../context/CartContext';

export interface OrderItem {
  id: string;
  name: string;
  price: number;
  discountedPrice?: number | null;
  quantity: number;
  image?: string;
}

export interface Address {
  title: string;
  address: string;
  city: string;
  district: string;
  neighborhood: string;
  phone: string;
}

export interface Order {
  id: string;
  userId: string;
  items: OrderItem[];
  total: number;
  address: Address;
  paymentMethod: string;
  status: 'pending' | 'processing' | 'shipped' | 'delivered' | 'cancelled';
  createdAt: Date;
  updatedAt: Date;
}

// Update product stock after order
export const updateProductStock = async (productId: string, soldQuantity: number): Promise<void> => {
  try {
    const productDoc = doc(db, 'products', productId);
    const productSnapshot = await getDoc(productDoc);
    
    if (productSnapshot.exists()) {
      const productData = productSnapshot.data();
      const currentStock = productData.stock || 0;
      const newStock = Math.max(0, currentStock - soldQuantity); // Ensure stock doesn't go below 0
      
      await updateDoc(productDoc, {
        stock: newStock
      });
    }
  } catch (error) {
    console.error('Error updating product stock:', error);
    throw error;
  }
};

// Create a new order
export const createOrder = async (orderData: Omit<Order, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> => {
  try {
    const orderWithTimestamps = {
      ...orderData,
      createdAt: new Date(),
      updatedAt: new Date()
    };
    
    const docRef = await addDoc(collection(db, 'orders'), orderWithTimestamps);
    
    // Update stock for each item in the order
    for (const item of orderData.items) {
      await updateProductStock(item.id, item.quantity);
    }
    
    return docRef.id;
  } catch (error) {
    console.error('Error creating order:', error);
    throw error;
  }
};

// Get orders for a specific user
export const getUserOrders = async (userId: string): Promise<Order[]> => {
  try {
    const ordersCollection = collection(db, 'orders');
    // Temporary fix: fetch all orders since composite index is required for userId + createdAt query
    const q = query(ordersCollection);
    const ordersSnapshot = await getDocs(q);

    // Filter orders by userId and sort by createdAt desc
    let orders = ordersSnapshot.docs.map(doc => {
      const data = doc.data();

      // Handle date conversion safely - Firestore timestamps have toDate() method
      // If it's already a regular Date or has toDate method, handle accordingly
      let createdAt;
      let updatedAt;

      if (data.createdAt && typeof data.createdAt.toDate === 'function') {
        // It's a Firestore Timestamp
        createdAt = data.createdAt.toDate();
      } else if (data.createdAt instanceof Date) {
        // It's already a JavaScript Date
        createdAt = data.createdAt;
      } else if (typeof data.createdAt === 'string' || typeof data.createdAt === 'number') {
        // It's a date string or timestamp
        createdAt = new Date(data.createdAt);
      } else {
        // Fallback to current time if invalid date
        createdAt = new Date();
      }

      if (data.updatedAt && typeof data.updatedAt.toDate === 'function') {
        // It's a Firestore Timestamp
        updatedAt = data.updatedAt.toDate();
      } else if (data.updatedAt instanceof Date) {
        // It's already a JavaScript Date
        updatedAt = data.updatedAt;
      } else if (typeof data.updatedAt === 'string' || typeof data.updatedAt === 'number') {
        // It's a date string or timestamp
        updatedAt = new Date(data.updatedAt);
      } else {
        // Fallback to current time if invalid date
        updatedAt = new Date();
      }

      return {
        id: doc.id,
        ...data,
        createdAt,
        updatedAt
      } as Order;
    });

    // Filter by userId and sort by createdAt desc
    orders = orders.filter(order => order.userId === userId);
    orders.sort((a, b) => {
      // Sort in descending order by createdAt
      return b.createdAt.getTime() - a.createdAt.getTime();
    });

    return orders;
  } catch (error) {
    console.error('Error fetching user orders:', error);
    // Return empty array instead of throwing to prevent errors when no orders exist
    return [];
  }
};

// Get a specific order
export const getOrderById = async (orderId: string): Promise<Order | null> => {
  try {
    const orderDoc = doc(db, 'orders', orderId);
    const orderSnapshot = await getDoc(orderDoc);
    
    if (orderSnapshot.exists()) {
      const data = orderSnapshot.data();
      
      // Handle date conversion safely
      let createdAt;
      let updatedAt;
      
      if (data.createdAt && typeof data.createdAt.toDate === 'function') {
        // It's a Firestore Timestamp
        createdAt = data.createdAt.toDate();
      } else if (data.createdAt instanceof Date) {
        // It's already a JavaScript Date
        createdAt = data.createdAt;
      } else if (typeof data.createdAt === 'string' || typeof data.createdAt === 'number') {
        // It's a date string or timestamp
        createdAt = new Date(data.createdAt);
      } else {
        // Fallback to current time if invalid date
        createdAt = new Date();
      }
      
      if (data.updatedAt && typeof data.updatedAt.toDate === 'function') {
        // It's a Firestore Timestamp
        updatedAt = data.updatedAt.toDate();
      } else if (data.updatedAt instanceof Date) {
        // It's already a JavaScript Date
        updatedAt = data.updatedAt;
      } else if (typeof data.updatedAt === 'string' || typeof data.updatedAt === 'number') {
        // It's a date string or timestamp
        updatedAt = new Date(data.updatedAt);
      } else {
        // Fallback to current time if invalid date
        updatedAt = new Date();
      }
      
      return {
        id: orderSnapshot.id,
        ...data,
        createdAt,
        updatedAt
      } as Order;
    }
    return null;
  } catch (error) {
    console.error('Error fetching order:', error);
    throw error;
  }
};

// Update order status
export const updateOrderStatus = async (orderId: string, status: Order['status']): Promise<void> => {
  try {
    const orderDoc = doc(db, 'orders', orderId);
    await updateDoc(orderDoc, {
      status,
      updatedAt: new Date()
    });
  } catch (error) {
    console.error('Error updating order status:', error);
    throw error;
  }
};

// Get all orders (for admin use)
export const getAllOrders = async (): Promise<Order[]> => {
  try {
    const ordersCollection = collection(db, 'orders');
    const q = query(ordersCollection, orderBy('createdAt', 'desc'));
    const ordersSnapshot = await getDocs(q);
    
    return ordersSnapshot.docs.map(doc => {
      const data = doc.data();
      
      // Handle date conversion safely
      let createdAt;
      let updatedAt;
      
      if (data.createdAt && typeof data.createdAt.toDate === 'function') {
        // It's a Firestore Timestamp
        createdAt = data.createdAt.toDate();
      } else if (data.createdAt instanceof Date) {
        // It's already a JavaScript Date
        createdAt = data.createdAt;
      } else if (typeof data.createdAt === 'string' || typeof data.createdAt === 'number') {
        // It's a date string or timestamp
        createdAt = new Date(data.createdAt);
      } else {
        // Fallback to current time if invalid date
        createdAt = new Date();
      }
      
      if (data.updatedAt && typeof data.updatedAt.toDate === 'function') {
        // It's a Firestore Timestamp
        updatedAt = data.updatedAt.toDate();
      } else if (data.updatedAt instanceof Date) {
        // It's already a JavaScript Date
        updatedAt = data.updatedAt;
      } else if (typeof data.updatedAt === 'string' || typeof data.updatedAt === 'number') {
        // It's a date string or timestamp
        updatedAt = new Date(data.updatedAt);
      } else {
        // Fallback to current time if invalid date
        updatedAt = new Date();
      }
      
      return {
        id: doc.id,
        ...data,
        createdAt,
        updatedAt
      } as Order;
    });
  } catch (error) {
    console.error('Error fetching all orders:', error);
    throw error;
  }
};

